import { routeStreetStats } from "./runnability";
import { Router, type Path } from "./astar";
import { LocalProjection, distance, haversine, type LatLon } from "./geo";
import { edgeCoords, type Graph } from "./graph";
import { HEAT_MIN_POP } from "./heat";
import {
  W_GREEN,
  W_POP,
  W_TRAIL,
  W_TRAIL_SCORE,
  W_WATER,
  edgeCosts,
  type CostOptions,
  trailsOn,
  type Prefs,
} from "./profiles/run";
import { EdgeIndex } from "./popularity";
import { mulberry32 } from "./rng";
import { NodeIndex } from "./snap";
import type { MatchedTrail, TrailLine } from "./trails";

/** Network distance / straight-line distance in a typical walk network (ADR 0002). */
const K = 1.25;
const REUSE_PENALTY = 3;
/** Cost multiplier on edges the user has already run, when "avoid my runs" is on. */
export const AVOID_PENALTY = 2.5;
/** Map-matching radius for the user's own GPS points. */
const RUN_MATCH_M = 20;
const MAX_EVALS = 6;
const TOLERANCE = 0.05;
/** Routes more similar than this (length-weighted edge Jaccard) are not both returned. */
const MAX_SIMILARITY = 0.6;
/** Trail options (ADR 0006): kinds offered, limits, overlay costs and the distance band. */
const OPTION_KINDS = new Set([1, 2, 3, 6]);
const TRAIL_MAX_LENGTH_M = 30_000;
/** Shorter named loops are not offered (the ADR 0006 loop rule; live: a 0 km elljusspår). */
const TRAIL_MIN_LENGTH_M = 400;
const TRAIL_MIN_MATCHED = 0.8;
const TRAIL_MAX_ACCESS_M = 2000;
const TRAIL_ACCESS_SHARE = 0.3;
const TRAIL_CANDIDATES = 5;
const TRAIL_ON_COST = 0.2;
const TRAIL_OFF_COST = 5;
/** A trail loop must run ≥ 80% on its trail. */
const TRAIL_MIN_ON = 0.8;
/** Options may miss D by ±25% (the distance score term is 0 inside the band). */
const TRAIL_BAND = 0.25;
/**
 * A normal route that beats a similar option takes its full label only when it runs ≥ 80% of the
 * option's length on the trail (laps counted); otherwise it is only "Via <name>".
 */
const TRAIL_FOLLOW_SHARE = 0.8;
/** Start and end closer than this are treated as a loop. */
const LOOP_SNAP_M = 75;
const LOOP_HEADINGS = 12;
const LOBE_HEADINGS = 6;
/** Score weight of the shape term (roundness for loops, detour spread for point-to-point). */
export const W_SHAPE = 0.4;

export interface PlanRequest {
  start: LatLon;
  end?: LatLon | null;
  distanceM: number;
  prefs: Prefs;
  seed?: number;
  /** The user's own imported points (flat lat, lon, lat, lon…); never leaves the device. */
  myRuns?: MyRuns | null;
  /** Points to pass through, in any order (at most MAX_WAYPOINTS). */
  waypoints?: LatLon[];
  /** Every route must pass every waypoint; otherwise at least one route does, when possible. */
  mustPass?: boolean;
}

export interface MyRuns {
  pts: Float64Array;
  /** Penalise edges the user has already run (x2.5). */
  avoid: boolean;
}

export interface RouteResult {
  /** [lat, lon] polyline from start to end. */
  coords: [number, number][];
  edges: number[];
  lengthM: number;
  climbM: number;
  descentM: number;
  /** Length-weighted mean popularity, 0..1. */
  popularity: number;
  /** Share of length on popular edges (pop ≥ 0.15, the heat-overlay threshold), 0..1. */
  popularShare: number;
  /** Length-weighted green share, 0..1. */
  green: number;
  /** Length-weighted share within 40 m of water, 0..1 (ADR 0007). */
  water: number;
  /** Traffic lights passed (ADR 0007; 0 where no lights data is loaded). */
  signals: number;
  /** Crossings of tertiary or busier roads. */
  busyCrossings: number;
  /** Share of length on edges the user has not run, 0..1. Only set when runs are imported. */
  newShare?: number;
  /** Share of length that repeats an edge already used, 0..1. */
  overlap: number;
  /** Shape quality 0..1: roundness for loops, detour spread for point-to-point. */
  spread: number;
  /** Share of length on running-trail edges (`trail > 0`, ADR 0006), 0..1. */
  trailShare: number;
  /** Named trails with ≥ 300 m on the route, longest first, at most 3. */
  trailNames: string[];
  /** Set on a trail option (loops only): which trail, how many laps, extended with a lobe. */
  trailOption?: TrailOption;
  /** Set when the request has waypoints: the route passes within WAYPOINT_PASS_M of all of them. */
  passesWaypoints?: boolean;
  score: number;
  shape: string;
  /** Elevation profile: cumulative distance (m) and elevation (m). */
  profile: { d: number; ele: number }[];
}

export interface TrailOption {
  /** Trail id (`r…`, `w…`, `n…`). */
  id: string;
  name: string;
  laps: number;
  /** An extra loop was added from the trail entry to reach the distance. */
  extended: boolean;
  /**
   * Set on a normal route that beat a similar option but does not follow the trail as far: it is
   * titled "Via X" and is not an option (no ±25% distance note).
   */
  via?: true;
}

/** Card title for a trail option: "Follow X", "n laps of X", "X + extra loop" or "Via X". */
export function trailOptionLabel(o: TrailOption): string {
  if (o.via) return `Via ${o.name}`;
  if (o.extended) return `${o.name} + extra loop`;
  return o.laps > 1 ? `${o.laps} laps of ${o.name}` : `Follow ${o.name}`;
}

export interface PlanResult {
  routes: RouteResult[];
  loop: boolean;
  warning?: string;
  stats: { candidates: number; evaluations: number; ms: number };
}

/** A via point; `node` pins it to a graph node (waypoints), otherwise it snaps to a junction. */
type Via = LatLon & { node?: number };
type Shape = { name: string; vias: (scale: number) => Via[] | null };

/** A route "passes" a waypoint when it comes this close. */
export const WAYPOINT_PASS_M = 40;
/** Waypoints farther than this from any path are ignored. */
export const WAYPOINT_SNAP_M = 300;
export const MAX_WAYPOINTS = 5;

interface Evaluated {
  shape: string;
  edges: number[];
  nodes: number[];
  length: number;
  cost: number;
}

/** Edge-cost weights for popularity and green share (ADR 0005), and running trails (ADR 0006). */
export interface Weights {
  pop: number;
  green: number;
  /** Trail cost weight, default W_TRAIL. */
  trail?: number;
  /** Trail score weight, default W_TRAIL_SCORE. */
  trailScore?: number;
  /** ADR 0007 switches (A/B checks); default all on. */
  cost?: CostOptions;
}

/** Named trails need this much length on a route to be listed on its card. */
const TRAIL_NAME_MIN_M = 300;

/** A Planner owns the router and index for one graph and can answer many requests. */
export class Planner {
  private readonly index: NodeIndex;
  /** Junctions only: via-points snapped to dead ends cause out-and-back spurs. */
  private readonly viaIndex: NodeIndex;
  private router: Router;
  /** Pure-length router for the "is D feasible?" check (ADR 0002 uses shortest length, not cost). */
  private readonly lengthRouter: Router;
  private prefsKey = "";
  private readonly penalty: Float32Array;
  /** Penalty with nothing in use: 1, or AVOID_PENALTY on edges the user has run. */
  private readonly basePenalty: Float32Array;
  private edgeIdx: EdgeIndex | null = null;
  private runKey = "";
  /** 1 on edges the user has run. */
  private ranEdges: Uint8Array | null = null;
  private avoiding = false;
  /** > 0 while trail options are scored (ADR 0006: distance term 0 inside ±band). */
  private distanceBand = 0;
  evaluations = 0;

  constructor(
    readonly g: Graph,
    readonly weights: Weights = { pop: W_POP, green: W_GREEN },
  ) {
    this.index = new NodeIndex(g);
    this.viaIndex = new NodeIndex(
      g,
      (n) => (g.adjStart[n + 1] as number) - (g.adjStart[n] as number) >= 3,
    );
    this.router = new Router(
      g,
      edgeCosts(g, { trail: 0, hills: 0 }, weights.pop, weights.green, this.wTrail, weights.cost),
    );
    this.penalty = new Float32Array(g.edgeCount).fill(1);
    this.basePenalty = new Float32Array(g.edgeCount).fill(1);
    const len = Float32Array.from(g.len);
    this.lengthRouter = new Router(g, { uv: len, vu: len, minPerMetre: 0.999 });
  }

  private get wTrail(): number {
    return this.weights.trail ?? W_TRAIL;
  }

  /** Recompute edge costs (after preferences, popularity or trails change). */
  setPrefs(prefs: Prefs, force = false): void {
    const key = `${prefs.trail}|${prefs.hills}|${trailsOn(prefs)}`;
    if (!force && key === this.prefsKey) return;
    this.prefsKey = key;
    this.router = new Router(
      this.g,
      edgeCosts(
        this.g,
        prefs,
        this.weights.pop,
        this.weights.green,
        this.wTrail,
        this.weights.cost,
      ),
    );
  }

  /** The graph's pop or trail arrays changed: the next plan recomputes edge costs. */
  invalidateCosts(): void {
    this.prefsKey = "";
  }

  /** The edge index (built once, shared with trail matching and "my runs"). */
  edgeIndex(): EdgeIndex {
    this.edgeIdx ??= new EdgeIndex(this.g);
    return this.edgeIdx;
  }

  /** Map-matches the user's points to edges (nearest edge within 20 m); cached per point set. */
  setMyRuns(runs: MyRuns | null | undefined): void {
    if (!runs || runs.pts.length < 2) {
      this.ranEdges = null;
      this.runKey = "";
      this.setAvoid(false);
      return;
    }
    const pts = runs.pts;
    // FNV-1a over the bits of every value: any change to the points changes the key.
    const words = new Uint32Array(pts.buffer, pts.byteOffset, pts.length * 2);
    let h = 0x811c9dc5;
    for (let i = 0; i < words.length; i++) h = Math.imul(h ^ (words[i] as number), 0x01000193);
    const key = `${pts.length}|${h >>> 0}`;
    if (key !== this.runKey) {
      this.runKey = key;
      const idx = this.edgeIndex();
      const ran = new Uint8Array(this.g.edgeCount);
      for (let i = 0; i + 1 < pts.length; i += 2) {
        const e = idx.match(pts[i] as number, pts[i + 1] as number, RUN_MATCH_M, null);
        if (e >= 0) ran[e] = 1;
      }
      this.ranEdges = ran;
      this.avoiding = !runs.avoid; // force a rebuild below
    }
    this.setAvoid(runs.avoid);
  }

  private setAvoid(on: boolean): void {
    if (on === this.avoiding) return;
    this.avoiding = on;
    const ran = this.ranEdges;
    for (let e = 0; e < this.basePenalty.length; e++) {
      const v = on && ran && ran[e] ? AVOID_PENALTY : 1;
      this.basePenalty[e] = v;
      this.penalty[e] = v;
    }
  }

  snap(p: LatLon): { node: number; distM: number } | null {
    return this.index.nearest(p.lat, p.lon);
  }

  plan(req: PlanRequest): PlanResult {
    const t0 = performance.now();
    this.evaluations = 0;
    this.setPrefs(req.prefs);
    this.setMyRuns(req.myRuns);
    const g = this.g;
    const s = this.snap(req.start);
    if (!s)
      return {
        routes: [],
        loop: true,
        warning: "No paths found near the start.",
        stats: this.stats(0, t0),
      };
    const endPt = req.end ?? null;
    const snappedEnd = !endPt || distance(req.start, endPt) < LOOP_SNAP_M ? s : this.snap(endPt);
    // Start and end on the same node (e.g. both far from any path) is a loop too.
    const loop = !endPt || snappedEnd === s || snappedEnd?.node === s.node;
    const e = loop ? s : snappedEnd;
    if (!e)
      return {
        routes: [],
        loop,
        warning: "No paths found near the end.",
        stats: this.stats(0, t0),
      };

    const S: LatLon = { lat: g.lat[s.node] as number, lon: g.lon[s.node] as number };
    const E: LatLon = { lat: g.lat[e.node] as number, lon: g.lon[e.node] as number };
    const D = req.distanceM;
    const wp = this.snapWaypoints(req.waypoints ?? []);
    const strict = req.mustPass === true && wp.nodes.length > 0;
    const notes = [...wp.warnings];

    if (!loop && wp.nodes.length === 0) {
      const direct = this.lengthRouter.route(s.node, e.node);
      if (!direct) {
        return {
          routes: [],
          loop,
          warning: "Start and end are not connected by runnable paths.",
          stats: this.stats(0, t0),
        };
      }
      if (D < direct.length * 1.02) {
        const r = this.describe({ shape: "direct", ...direct }, D, loop, req.prefs);
        return {
          routes: [r],
          loop,
          warning: `The distance is shorter than the direct route (${(direct.length / 1000).toFixed(1)} km).`,
          stats: this.stats(1, t0),
        };
      }
    }

    const shapes = loop
      ? loopShapes(S, D, req.seed ?? 0)
      : pointToPointShapes(S, E, D, req.seed ?? 0);
    const results: Evaluated[] = [];
    for (const shape of shapes) {
      const best = this.fit(shape, s.node, e.node, D);
      if (best) results.push(best);
    }

    // Candidates within tolerance always rank before those outside it.
    const fits = (r: RouteResult) => Math.abs(r.lengthM - D) <= TOLERANCE * D;
    const rank = (rs: Evaluated[]) => {
      const described = rs.map((r) => this.describe(r, D, loop, req.prefs));
      described.sort((a, b) => a.score - b.score);
      return [...described.filter(fits), ...described.filter((r) => !fits(r))];
    };
    const through =
      wp.nodes.length > 0 ? rank(this.waypointCandidates(s.node, e.node, wp, D, loop, shapes)) : [];
    let picked = strict ? [] : diverse(rank(results), 3, MAX_SIMILARITY, g);
    if (!strict && loop && trailsOn(req.prefs) && g.trails.length > 0) {
      const option = this.trailOption(s.node, D, req.prefs, req.seed ?? 0);
      if (option) picked = withTrailOption(picked, option, g);
    }
    if (wp.nodes.length > 0) {
      const passing = (r: RouteResult) => wp.pts.every((p) => passesNear(r, p));
      if (strict) {
        picked = diverse(through, 3, MAX_SIMILARITY, g);
      } else if (!picked.some(passing) && through[0]) {
        // At least one route goes through the waypoints: it replaces the last one.
        picked = [...picked.slice(0, 2), through[0]];
      }
      for (const r of picked) r.passesWaypoints = passing(r);
      const best = through[0];
      if (best && !fits(best) && best.lengthM > D) {
        notes.push(`Passing the waypoints needs at least ${(best.lengthM / 1000).toFixed(1)} km.`);
      } else if (!best) {
        notes.push("No route could be found through the waypoints.");
      }
    }
    const warning = notes.length > 0 ? notes.join(" ") : undefined;
    return {
      routes: picked,
      loop,
      ...(warning ? { warning } : {}),
      stats: this.stats(shapes.length, t0),
    };
  }

  /** Waypoints snapped to the nearest node within WAYPOINT_SNAP_M; far ones are dropped. */
  private snapWaypoints(pts: LatLon[]): { nodes: number[]; pts: LatLon[]; warnings: string[] } {
    const nodes: number[] = [];
    const kept: LatLon[] = [];
    const warnings: string[] = [];
    pts.slice(0, MAX_WAYPOINTS).forEach((p, i) => {
      const n = this.index.nearest(p.lat, p.lon);
      if (!n || n.distM > WAYPOINT_SNAP_M) {
        warnings.push(`No paths near waypoint ${i + 1}; it was ignored.`);
        return;
      }
      nodes.push(n.node);
      kept.push({ lat: this.g.lat[n.node] as number, lon: this.g.lon[n.node] as number });
    });
    return { nodes, pts: kept, warnings };
  }

  /**
   * Routes through every waypoint: the shortest visiting order (straight-line, by permutation),
   * then, when that is shorter than D, one sideways bulge on a leg (each leg, both sides) fitted
   * to D like the normal shapes.
   */
  private waypointCandidates(
    s: number,
    e: number,
    wp: { nodes: number[]; pts: LatLon[] },
    D: number,
    loop: boolean,
    shapes: Shape[],
  ): Evaluated[] {
    const g = this.g;
    const at = (n: number): LatLon => ({ lat: g.lat[n] as number, lon: g.lon[n] as number });
    const order = bestOrder(at(s), at(e), wp.pts);
    const fixed: Via[] = order.map((i) => ({
      ...(wp.pts[i] as LatLon),
      node: wp.nodes[i] as number,
    }));
    const base = this.evaluate({ name: "waypoints", vias: () => fixed }, 1, s, e);
    if (!base) return [];
    const out: Evaluated[] = [base];
    if (base.length >= D * (1 - TOLERANCE)) return out;
    // The normal shapes with the waypoints inserted where they add the least straight length.
    for (const shape of shapes) {
      const r = this.fit(
        {
          name: `${shape.name}+waypoints`,
          vias: (scale) => {
            const v = shape.vias(scale);
            return v ? insertCheapest(at(s), at(e), v, fixed) : null;
          },
        },
        s,
        e,
        D,
      );
      if (r) out.push(r);
    }
    const stops: LatLon[] = [at(s), ...fixed, at(e)];
    const proj = new LocalProjection(at(s));
    const extra = D - base.length;
    for (let i = 0; i + 1 < stops.length; i++) {
      const a = proj.toXY(stops[i] as LatLon);
      const b = proj.toXY(stops[i + 1] as LatLon);
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy);
      // A loop with one waypoint has two legs on one line; a leg of length 0 (start = end) has none.
      if (len < 1 && !loop) continue;
      const nx = len >= 1 ? -dy / len : 1;
      const ny = len >= 1 ? dx / len : 0;
      for (const side of [1, -1]) {
        const shape: Shape = {
          name: `waypoints-bulge-${i}${side > 0 ? "l" : "r"}`,
          vias: (scale) => {
            const h = (scale * extra) / 2;
            const m = proj.toLatLon(
              (a.x + b.x) / 2 + side * nx * h,
              (a.y + b.y) / 2 + side * ny * h,
            );
            const vias = fixed.slice();
            vias.splice(i, 0, m);
            return vias;
          },
        };
        const r = this.fit(shape, s, e, D);
        if (r) out.push(r);
      }
    }
    return out;
  }

  private stats(candidates: number, t0: number) {
    return { candidates, evaluations: this.evaluations, ms: Math.round(performance.now() - t0) };
  }

  /** Proportional step, then secant on the shape scale until within tolerance. */
  private fit(shape: Shape, s: number, e: number, D: number): Evaluated | null {
    let best: Evaluated | null = null;
    let prev: { x: number; y: number } | null = null;
    let x = 1;
    for (let i = 0; i < MAX_EVALS; i++) {
      const r = this.evaluate(shape, x, s, e);
      if (!r) return best;
      if (!best || Math.abs(r.length - D) < Math.abs(best.length - D)) best = r;
      if (Math.abs(r.length - D) <= TOLERANCE * D) break;
      let next: number;
      if (prev && Math.abs(r.length - prev.y) > 1) {
        next = x + ((D - r.length) * (x - prev.x)) / (r.length - prev.y);
      } else {
        next = x * (D / Math.max(1, r.length));
      }
      // Keep steps sane: the length/scale relation is noisy on real networks.
      next = Math.min(Math.max(next, x * 0.4, 0.05), x * 2.5);
      prev = { x, y: r.length };
      if (Math.abs(next - x) < 1e-3) break;
      x = next;
    }
    return best;
  }

  /** Route S → vias → E with edge-reuse penalties. */
  private evaluate(shape: Shape, scale: number, s: number, e: number): Evaluated | null {
    this.evaluations++;
    const vias = shape.vias(scale);
    if (!vias) return null;
    const stops = [s];
    for (const v of vias) {
      const node = v.node ?? this.viaIndex.nearest(v.lat, v.lon)?.node;
      if (node !== undefined && node !== stops[stops.length - 1]) stops.push(node);
    }
    if (stops[stops.length - 1] !== e || stops.length === 1) stops.push(e);
    return this.routeStops(this.router, stops, shape.name);
  }

  /**
   * Routes through `stops` in order, multiplying the penalty of every edge a leg uses by 3 for the
   * following legs. The penalties are restored to their values before the call (so a caller may
   * pre-penalise edges, as trail extensions do).
   */
  private routeStops(router: Router, stops: number[], name: string): Evaluated | null {
    const penalty = this.penalty;
    const touched: number[] = [];
    const old: number[] = [];
    const edges: number[] = [];
    const nodes: number[] = [stops[0] as number];
    let length = 0;
    let cost = 0;
    let ok = true;
    for (let i = 1; i < stops.length; i++) {
      const a = stops[i - 1] as number;
      const b = stops[i] as number;
      if (a === b) continue;
      const leg: Path | null = router.route(a, b, penalty);
      if (!leg) {
        ok = false;
        break;
      }
      for (const ed of leg.edges) {
        old.push(penalty[ed] as number);
        touched.push(ed);
        penalty[ed] = (penalty[ed] as number) * REUSE_PENALTY;
        edges.push(ed);
      }
      for (let j = 1; j < leg.nodes.length; j++) nodes.push(leg.nodes[j] as number);
      length += leg.length;
      cost += leg.cost;
    }
    for (let i = touched.length - 1; i >= 0; i--) penalty[touched[i] as number] = old[i] as number;
    if (!ok) return null;
    return { shape: name, edges, nodes, length, cost };
  }

  // ---- Trail options (ADR 0006; ARCHITECTURE "Trail options") -------------------------------

  /** Trail-overlay router: the current trail's edges cost len × 0.2, all others len × 5. */
  private overlay: { router: Router; uv: Float32Array } | null = null;
  /** Node marks for the current trail (generation stamps). */
  private trailMark: Int32Array | null = null;
  private trailGen = 0;

  /** The best trail option for a loop from `s`, scored against the ±25% band, or null. */
  private trailOption(s: number, D: number, prefs: Prefs, seed: number): RouteResult | null {
    const g = this.g;
    const maxAccess = Math.min(TRAIL_MAX_ACCESS_M, TRAIL_ACCESS_SHARE * D);
    const sLat = g.lat[s] as number;
    const sLon = g.lon[s] as number;
    const near: { t: number; d: number }[] = [];
    g.trails.forEach((t, i) => {
      if (!OPTION_KINDS.has(t.kind) || !t.name || !t.loop || !t.inside || !t.line) return;
      if (t.lengthM > TRAIL_MAX_LENGTH_M || t.lengthM < TRAIL_MIN_LENGTH_M) return;
      if (t.matchedM < TRAIL_MIN_MATCHED * t.lengthM) return;
      let d = Infinity;
      for (const e of t.edges) {
        for (const n of [g.u[e] as number, g.v[e] as number])
          d = Math.min(d, haversine(sLat, sLon, g.lat[n] as number, g.lon[n] as number));
      }
      if (d <= maxAccess) near.push({ t: i, d });
    });
    if (near.length === 0) return null;
    near.sort((a, b) => a.d - b.d || a.t - b.t);
    let best: RouteResult | null = null;
    this.distanceBand = TRAIL_BAND;
    try {
      for (const { t } of near.slice(0, TRAIL_CANDIDATES)) {
        const r = this.trailCandidate(t, s, D, maxAccess, prefs, seed);
        if (r && (!best || r.score < best.score)) best = r;
      }
    } finally {
      this.distanceBand = 0;
    }
    return best;
  }

  private trailCandidate(
    t: number,
    s: number,
    D: number,
    maxAccess: number,
    prefs: Prefs,
    seed: number,
  ): RouteResult | null {
    const g = this.g;
    const trail = g.trails[t] as MatchedTrail;
    const mark = (this.trailMark ??= new Int32Array(g.nodeCount));
    const tag = ++this.trailGen;
    const onTrail = new Set<number>(trail.edges);
    for (const e of trail.edges) {
      mark[g.u[e] as number] = tag;
      mark[g.v[e] as number] = tag;
    }
    // 1. Entry X: the trail node nearest the start by network, on the normal costs.
    const access = this.router.routeToAny(s, mark, tag, this.basePenalty);
    if (!access || access.length > maxAccess) return null;
    const X = access.nodes[access.nodes.length - 1] as number;

    // 2. The trail loop X → P1 → P2 → X on the overlay costs.
    const line = trail.line as TrailLine;
    const proj = new LocalProjection({ lat: g.lat[X] as number, lon: g.lon[X] as number });
    const cum = new Float64Array(line.lat.length);
    let x0 = 0;
    let bestD = Infinity;
    for (let i = 0; i < line.lat.length; i++) {
      if (i > 0) {
        const a = proj.toXY({ lat: line.lat[i - 1] as number, lon: line.lon[i - 1] as number });
        const b = proj.toXY({ lat: line.lat[i] as number, lon: line.lon[i] as number });
        cum[i] = (cum[i - 1] as number) + Math.hypot(b.x - a.x, b.y - a.y);
      }
      const p = proj.toXY({ lat: line.lat[i] as number, lon: line.lon[i] as number });
      const d = Math.hypot(p.x, p.y);
      if (d < bestD) {
        bestD = d;
        x0 = cum[i] as number;
      }
    }
    const L = cum[line.lat.length - 1] as number;
    if (L <= 0) return null;
    const stops = [X];
    for (const f of [1 / 3, 2 / 3]) {
      const pt = pointAt(line, cum, (x0 + f * L) % L);
      const n = this.nearestMarked(pt.lat, pt.lon, trail, mark, tag);
      if (n >= 0 && n !== stops[stops.length - 1]) stops.push(n);
    }
    stops.push(X);
    if (stops.length < 4) return null;
    const ov = this.overlayRouter(trail, true);
    let loop: Evaluated | null;
    try {
      loop = this.routeStops(ov, stops, `trail:${trail.id}`);
    } finally {
      this.overlayRouter(trail, false);
    }
    if (!loop || loop.length < 0.95 * TRAIL_MIN_LENGTH_M) return null;
    let on = 0;
    for (const e of loop.edges) if (onTrail.has(e)) on += g.len[e] as number;
    if (on < TRAIL_MIN_ON * loop.length) return null;
    const Lt = loop.length;
    const a = access.length;

    // 3. Laps: n ∈ {1, 2, 3} closest to D, accepted within ±25%.
    let laps = 1;
    for (const n of [2, 3]) {
      if (Math.abs(2 * a + n * Lt - D) < Math.abs(2 * a + laps * Lt - D)) laps = n;
    }
    const back = reversePath(access);
    const name = trail.name as string;
    if (Math.abs(2 * a + laps * Lt - D) <= TRAIL_BAND * D) {
      const parts = [access, ...Array.from({ length: laps }, () => loop), back];
      return this.describeOption(parts, D, prefs, { id: trail.id, name, laps, extended: false });
    }
    // 4. Extension: a lobe from X fitted to the rest of D, with the trail loop already penalised.
    // Only when one lap falls short of the band. The "> 1.25·D" test is implied by ">= 0.75·D" and
    // is kept only to mirror the contract's wording (totals > 1.25·D with n = 1 are rejected).
    if (2 * a + Lt > (1 + TRAIL_BAND) * D || 2 * a + Lt >= (1 - TRAIL_BAND) * D) return null;
    const rest = D - 2 * a - Lt;
    const used = [...access.edges, ...loop.edges];
    const old = used.map((e) => this.penalty[e] as number);
    for (const e of used) this.penalty[e] = (this.penalty[e] as number) * REUSE_PENALTY;
    let lobe: Evaluated | null = null;
    try {
      // Away from the trail: the lobe explores new ground.
      const c = proj.toXY({
        lat: (trail.bbox.south + trail.bbox.north) / 2,
        lon: (trail.bbox.west + trail.bbox.east) / 2,
      });
      const away = Math.atan2(-c.y, -c.x);
      const spin = (mulberry32(seed ^ 0x51ed)() - 0.5) * (Math.PI / 3);
      for (const turn of [0, Math.PI / 3, -Math.PI / 3]) {
        const shape = lobeShape(
          { lat: g.lat[X] as number, lon: g.lon[X] as number },
          rest,
          away + spin + turn,
        );
        const r = this.fit(shape, X, X, rest);
        if (r && (!lobe || Math.abs(r.length - rest) < Math.abs(lobe.length - rest))) lobe = r;
        if (lobe && Math.abs(lobe.length - rest) <= TOLERANCE * rest) break;
      }
    } finally {
      for (let i = used.length - 1; i >= 0; i--) this.penalty[used[i] as number] = old[i] as number;
    }
    if (!lobe) return null;
    const total = 2 * a + Lt + lobe.length;
    if (Math.abs(total - D) > TRAIL_BAND * D) return null;
    return this.describeOption([access, loop, lobe, back], D, prefs, {
      id: trail.id,
      name,
      laps: 1,
      extended: true,
    });
  }

  /** Nearest node of the trail (an end of one of its edges) to a point, or -1. */
  private nearestMarked(
    lat: number,
    lon: number,
    trail: MatchedTrail,
    mark: Int32Array,
    tag: number,
  ): number {
    const g = this.g;
    let best = -1;
    let bestD = Infinity;
    for (const e of trail.edges) {
      for (const n of [g.u[e] as number, g.v[e] as number]) {
        if (mark[n] !== tag) continue;
        const d = haversine(lat, lon, g.lat[n] as number, g.lon[n] as number);
        if (d < bestD) {
          bestD = d;
          best = n;
        }
      }
    }
    return best;
  }

  /** Overlay router for `trail` (on = set its edges to len × 0.2, off = restore len × 5). */
  private overlayRouter(trail: MatchedTrail, on: boolean): Router {
    const g = this.g;
    if (!this.overlay) {
      const uv = new Float32Array(g.edgeCount);
      for (let e = 0; e < g.edgeCount; e++) uv[e] = (g.len[e] as number) * TRAIL_OFF_COST;
      this.overlay = {
        uv,
        router: new Router(g, { uv, vu: uv, minPerMetre: TRAIL_ON_COST * 0.999 }),
      };
    }
    const { uv, router } = this.overlay;
    const f = on ? TRAIL_ON_COST : TRAIL_OFF_COST;
    for (const e of trail.edges) uv[e] = (g.len[e] as number) * f;
    return router;
  }

  /** Joins path parts into one route and describes it as a trail option. */
  private describeOption(
    parts: { edges: number[]; nodes: number[]; length: number }[],
    D: number,
    prefs: Prefs,
    option: TrailOption,
  ): RouteResult {
    const g = this.g;
    const { uv, vu } = this.router.costs;
    const edges: number[] = [];
    const nodes: number[] = [parts[0]?.nodes[0] as number];
    let length = 0;
    let cost = 0;
    for (const p of parts) {
      for (let i = 0; i < p.edges.length; i++) {
        const e = p.edges[i] as number;
        const from = p.nodes[i] as number;
        edges.push(e);
        nodes.push(p.nodes[i + 1] as number);
        cost += (g.u[e] as number) === from ? (uv[e] as number) : (vu[e] as number);
      }
      length += p.length;
    }
    const r = this.describe(
      { shape: `trail:${option.id}`, edges, nodes, length, cost },
      D,
      true,
      prefs,
    );
    return { ...r, trailOption: option };
  }

  /** Score term for the distance error; while trail options are scored, 0 inside ±band. */
  private distanceTerm(length: number, D: number): number {
    const err = Math.abs(length - D) / D;
    return 3 * (this.distanceBand > 0 ? Math.max(0, err - this.distanceBand) : err);
  }

  /** Named trails with ≥ 300 m on these edges, longest first, at most 3 (one entry per name). */
  private trailNames(edges: number[]): string[] {
    const g = this.g;
    if (g.trails.length === 0) return [];
    const byName = new Map<string, number>();
    for (const e of edges) {
      const t = g.trailId[e] as number;
      const name = t >= 0 ? g.trails[t]?.name : null;
      if (!name) continue;
      byName.set(name, (byName.get(name) ?? 0) + (g.len[e] as number));
    }
    return [...byName]
      .filter(([, m]) => m >= TRAIL_NAME_MIN_M)
      .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
      .slice(0, 3)
      .map(([n]) => n);
  }

  private describe(r: Evaluated, D: number, loop: boolean, prefs: Prefs): RouteResult {
    const g = this.g;
    const coords: [number, number][] = [];
    const profile: { d: number; ele: number }[] = [];
    let climb = 0;
    let descent = 0;
    let pop = 0;
    let popular = 0;
    let green = 0;
    let water = 0;
    let repeated = 0;
    let onTrail = 0;
    let trailSum = 0;
    let dist = 0;
    const used = new Set<number>();
    const ran = this.ranEdges;
    let fresh = 0;
    let cur = r.nodes[0] as number;
    profile.push({ d: 0, ele: g.ele[cur] as number });
    r.edges.forEach((e, i) => {
      const len = g.len[e] as number;
      const forward = (g.u[e] as number) === cur;
      climb += forward ? (g.climbUV[e] as number) : (g.climbVU[e] as number);
      descent += forward ? (g.climbVU[e] as number) : (g.climbUV[e] as number);
      pop += len * (g.pop[e] as number);
      if ((g.pop[e] as number) >= HEAT_MIN_POP) popular += len;
      green += len * (g.green[e] as number);
      water += len * (g.water[e] as number);
      if ((g.trail[e] as number) > 0) onTrail += len;
      trailSum += len * (g.trailS[e] as number);
      if (used.has(e)) repeated += len;
      if (!ran || !ran[e]) fresh += len;
      used.add(e);
      const pts = edgeCoords(g, e, cur);
      for (let k = i === 0 ? 0 : 1; k < pts.length; k++) coords.push(pts[k] as [number, number]);
      cur = r.nodes[i + 1] as number;
      dist += len;
      profile.push({ d: dist, ele: g.ele[cur] as number });
    });
    if (coords.length === 0) coords.push([g.lat[cur] as number, g.lon[cur] as number]);

    const L = Math.max(1, r.length);
    const popularity = pop / L;
    const greenShare = green / L;
    const waterShare = Math.min(1, water / L);
    const street = routeStreetStats(g, r.edges, r.nodes[0] as number);
    const overlap = repeated / L;
    const climbPerKm = (climb / L) * 1000;
    const hillTerm =
      prefs.hills < 0
        ? (-prefs.hills * climbPerKm) / 30
        : prefs.hills > 0
          ? prefs.hills * (1 - Math.min(1, climbPerKm / 30))
          : 0;
    // One shape term: roundness for loops, detour spread (its point-to-point twin) otherwise.
    const spread = loop ? roundness(coords) : detourSpread(coords);
    const costPerM = r.cost / L;
    const trailScore = trailSum / L;
    const trailTerm = trailsOn(prefs)
      ? (this.weights.trailScore ?? W_TRAIL_SCORE) * (1 - Math.min(1, trailScore))
      : 0;
    const score =
      this.distanceTerm(r.length, D) +
      2 * overlap +
      0.6 * (1 - popularity) +
      0.4 * (1 - greenShare) +
      (this.weights.cost?.water ?? W_WATER) * (1 - waterShare) +
      0.5 * hillTerm +
      W_SHAPE * (1 - spread) +
      trailTerm +
      0.5 * Math.max(0, costPerM - 1);

    return {
      coords,
      edges: r.edges,
      lengthM: r.length,
      climbM: climb,
      descentM: descent,
      popularity,
      popularShare: Math.min(1, popular / L),
      green: greenShare,
      water: waterShare,
      signals: street.signals,
      busyCrossings: street.busyCrossings,
      ...(ran ? { newShare: Math.min(1, fresh / L) } : {}),
      overlap,
      spread,
      trailShare: Math.min(1, onTrail / L),
      trailNames: this.trailNames(r.edges),
      score,
      shape: r.shape,
      profile: downsample(profile, 200),
    };
  }
}

/**
 * Adds the trail option to the picked routes (ARCHITECTURE "Trail options" step 5). A normal route
 * with Jaccard > 0.6 to it competes with it: the better score stays. A kept normal route takes the
 * option's label only when it follows the trail (named on it, and ≥ 80% of the option's length on
 * the trail, laps counted); when it is only named on it, it is "Via X"; otherwise it has no label.
 * Without a rival, the option takes slot 3, or a higher slot when it scores better.
 */
export function withTrailOption(
  picked: RouteResult[],
  option: RouteResult,
  g: Graph,
): RouteResult[] {
  const set = new Set(option.edges);
  const similar = picked.filter((r) => jaccard(new Set(r.edges), set, g) > MAX_SIMILARITY);
  if (similar.length > 0) {
    const rival = similar.reduce((a, b) => (b.score < a.score ? b : a));
    if (rival.score <= option.score) {
      const label = followLabel(rival, option, g);
      return picked.map((r) => (r === rival && label ? { ...r, trailOption: label } : r));
    }
  }
  const out = picked.filter((r) => !similar.includes(r)).slice(0, 2);
  let i = out.length;
  while (i > 0 && option.score < (out[i - 1] as RouteResult).score) i--;
  out.splice(i, 0, option);
  return out;
}

/** The label a normal route that beat `option` may carry (see `withTrailOption`), or undefined. */
function followLabel(r: RouteResult, option: RouteResult, g: Graph): TrailOption | undefined {
  const label = option.trailOption;
  if (!label || label.via || !r.trailNames.includes(label.name)) return undefined;
  const t = g.trails.findIndex((x) => x.id === label.id);
  if (t < 0) return undefined;
  const onTrail = (edges: number[]) => {
    let m = 0;
    for (const e of edges) if (g.trailId[e] === t) m += g.len[e] as number;
    return m;
  };
  const need = TRAIL_FOLLOW_SHARE * onTrail(option.edges);
  return need > 0 && onTrail(r.edges) >= need ? label : { ...label, via: true };
}

/** The point at arc length `at` along a line with cumulative lengths `cum`. */
function pointAt(line: TrailLine, cum: Float64Array, at: number): LatLon {
  let i = 1;
  while (i < cum.length - 1 && (cum[i] as number) < at) i++;
  const a = cum[i - 1] as number;
  const b = cum[i] as number;
  const f = b > a ? Math.min(1, Math.max(0, (at - a) / (b - a))) : 0;
  return {
    lat: (line.lat[i - 1] as number) + f * ((line.lat[i] as number) - (line.lat[i - 1] as number)),
    lon: (line.lon[i - 1] as number) + f * ((line.lon[i] as number) - (line.lon[i - 1] as number)),
  };
}

function reversePath(p: Path): Path {
  return { ...p, nodes: [...p.nodes].reverse(), edges: [...p.edges].reverse() };
}

/**
 * A lobe through X of length D (the point-to-point lobe shape): a circle of circumference
 * scale · D / k through X, centred at heading θ, with two via-points at 120° and 240°.
 */
export function lobeShape(X: LatLon, D: number, theta: number): Shape {
  const proj = new LocalProjection(X);
  return {
    name: `lobe@${Math.round((theta * 180) / Math.PI)}°`,
    vias: (scale) => {
      const R = (scale * D) / K / (2 * Math.PI);
      if (R <= 0) return null;
      const cx = R * Math.cos(theta);
      const cy = R * Math.sin(theta);
      const back = theta + Math.PI;
      return [1, 2].map((q) => {
        const a = back + (q * 2 * Math.PI) / 3;
        return proj.toLatLon(cx + R * Math.cos(a), cy + R * Math.sin(a));
      });
    },
  };
}

/** Isoperimetric quotient 4πA/P² of a closed polyline (1 = circle). */
export function roundness(coords: [number, number][]): number {
  if (coords.length < 4) return 0;
  const c0 = coords[0] as [number, number];
  const proj = new LocalProjection({ lat: c0[0], lon: c0[1] });
  let area = 0;
  let perim = 0;
  let prev = proj.toXY({ lat: c0[0], lon: c0[1] });
  for (let i = 1; i <= coords.length; i++) {
    const c = coords[i % coords.length] as [number, number];
    const p = proj.toXY({ lat: c[0], lon: c[1] });
    area += prev.x * p.y - p.x * prev.y;
    perim += Math.hypot(p.x - prev.x, p.y - prev.y);
    prev = p;
  }
  area = Math.abs(area) / 2;
  return perim > 0 ? Math.min(1, (4 * Math.PI * area) / (perim * perim)) : 0;
}

/**
 * Detour spread of an open polyline S → E, 0..1: the area between the route and the straight
 * line E → S (shoelace, absolute), divided by the largest area any curve of the same length
 * through S and E can enclose with that line, which is a circular arc (Dido's problem).
 * An arc scores 1, an out-and-back spur or zigzag along the direct line scores ≈ 0.
 * A direct route (no extra length) scores 1. For S = E it equals `roundness`.
 */
export function detourSpread(coords: [number, number][]): number {
  if (coords.length < 3) return 1;
  const c0 = coords[0] as [number, number];
  const proj = new LocalProjection({ lat: c0[0], lon: c0[1] });
  const kx = proj.mPerDegLon;
  const ky = proj.mPerDegLat;
  let area = 0;
  let len = 0;
  let px = 0;
  let py = 0;
  for (let i = 1; i < coords.length; i++) {
    const c = coords[i] as [number, number];
    const x = (c[1] - c0[1]) * kx;
    const y = (c[0] - c0[0]) * ky;
    area += px * y - x * py;
    len += Math.hypot(x - px, y - py);
    px = x;
    py = y;
  }
  // The closing edge E → S ends at the origin, so it adds nothing to the shoelace sum.
  const d = Math.hypot(px, py);
  if (len - d <= 1e-6 * Math.max(1, len)) return 1;
  const phi = arcAngle(d, len);
  const R = len / phi;
  const maxArea = ((R * R) / 2) * (phi - Math.sin(phi));
  return maxArea > 0 ? Math.min(1, Math.abs(area) / 2 / maxArea) : 1;
}

/** Central angle φ in (0, 2π) of the circular arc of length L on a chord d ≤ L. */
function arcAngle(d: number, L: number): number {
  // Solve sin(φ/2)/(φ/2) = d/L by bisection (the left side is decreasing).
  const target = d / L;
  let lo = 1e-9;
  let hi = 2 * Math.PI - 1e-9;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (Math.sin(mid / 2) / (mid / 2) > target) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

function downsample<T>(xs: T[], max: number): T[] {
  if (xs.length <= max) return xs;
  const out: T[] = [];
  for (let i = 0; i < max; i++) out.push(xs[Math.round((i * (xs.length - 1)) / (max - 1))] as T);
  return out;
}

/** Greedy top-n with pairwise length-weighted edge Jaccard ≤ maxSim. */
/** Does the route come within WAYPOINT_PASS_M of p? */
function passesNear(r: RouteResult, p: LatLon): boolean {
  return r.coords.some(([lat, lon]) => distance({ lat, lon }, p) <= WAYPOINT_PASS_M);
}

/**
 * Inserts each pinned waypoint into the via list S → vias → E where it adds the least straight
 * length, keeping the waypoints' own order (`fixed` is already in the best order).
 */
export function insertCheapest(S: LatLon, E: LatLon, vias: Via[], fixed: Via[]): Via[] {
  const out = vias.slice();
  let from = 0; // a waypoint goes after the previous one, so their order holds
  for (const w of fixed) {
    let bestAt = out.length;
    let bestAdd = Infinity;
    for (let k = from; k <= out.length; k++) {
      const a = k === 0 ? S : (out[k - 1] as LatLon);
      const b = k === out.length ? E : (out[k] as LatLon);
      const add = distance(a, w) + distance(w, b) - distance(a, b);
      if (add < bestAdd) {
        bestAdd = add;
        bestAt = k;
      }
    }
    out.splice(bestAt, 0, w);
    from = bestAt + 1;
  }
  return out;
}

/** Visiting order of `pts` from S to E (E = S for a loop) with the least straight-line length. */
export function bestOrder(S: LatLon, E: LatLon, pts: LatLon[]): number[] {
  const idx = pts.map((_, i) => i);
  let best = idx.slice();
  let bestLen = Infinity;
  const permute = (k: number) => {
    if (k === idx.length) {
      let len = 0;
      let prev = S;
      for (const i of idx) {
        len += distance(prev, pts[i] as LatLon);
        prev = pts[i] as LatLon;
      }
      len += distance(prev, E);
      if (len < bestLen) {
        bestLen = len;
        best = idx.slice();
      }
      return;
    }
    for (let j = k; j < idx.length; j++) {
      [idx[k], idx[j]] = [idx[j] as number, idx[k] as number];
      permute(k + 1);
      [idx[k], idx[j]] = [idx[j] as number, idx[k] as number];
    }
  };
  permute(0);
  return best;
}

export function diverse(sorted: RouteResult[], n: number, maxSim: number, g: Graph): RouteResult[] {
  const out: RouteResult[] = [];
  const sets: Set<number>[] = [];
  for (const r of sorted) {
    const set = new Set(r.edges);
    const tooClose = sets.some((other) => jaccard(set, other, g) > maxSim);
    if (tooClose) continue;
    out.push(r);
    sets.push(set);
    if (out.length === n) break;
  }
  return out;
}

export function jaccard(a: Set<number>, b: Set<number>, g: Graph): number {
  let inter = 0;
  let union = 0;
  for (const e of a) {
    const l = g.len[e] as number;
    union += l;
    if (b.has(e)) inter += l;
  }
  for (const e of b) if (!a.has(e)) union += g.len[e] as number;
  return union > 0 ? inter / union : 0;
}

// ---------------------------------------------------------------------------------------------
// Shapes (ADR 0002). All maths in a local metric projection centred on the start.

/** Loops: circle through S with centre at heading θ; 3 via-points at 90°, 180°, 270°. */
export function loopShapes(S: LatLon, D: number, seed: number): Shape[] {
  const proj = new LocalProjection(S);
  const offset = mulberry32(seed)() * ((2 * Math.PI) / LOOP_HEADINGS);
  const shapes: Shape[] = [];
  for (let h = 0; h < LOOP_HEADINGS; h++) {
    const theta = offset + (h * 2 * Math.PI) / LOOP_HEADINGS;
    shapes.push({
      name: `loop@${Math.round((theta * 180) / Math.PI)}°`,
      vias: (scale) => {
        const R = (scale * D) / (2 * Math.PI * K);
        const cx = R * Math.cos(theta);
        const cy = R * Math.sin(theta);
        const back = theta + Math.PI;
        return [1, 2, 3].map((q) => {
          const a = back + (q * Math.PI) / 2;
          return proj.toLatLon(cx + R * Math.cos(a), cy + R * Math.sin(a));
        });
      },
    });
  }
  return shapes;
}

/** Point-to-point: arcs through S and E (both sides, 1 or 2 vias) + lobes at S or E. */
export function pointToPointShapes(S: LatLon, E: LatLon, D: number, seed: number): Shape[] {
  const proj = new LocalProjection(S);
  const e = proj.toXY(E);
  const d = Math.hypot(e.x, e.y);
  const ux = e.x / d;
  const uy = e.y / d;
  const nx = -uy;
  const ny = ux;
  const shapes: Shape[] = [];

  for (const side of [1, -1]) {
    for (const nVia of [1, 2]) {
      shapes.push({
        name: `arc${side > 0 ? "L" : "R"}${nVia}`,
        vias: (scale) => {
          const L = (scale * D) / K;
          if (L <= d * 1.01) return [];
          const pts = arcPoints(d, L, nVia);
          return pts.map(([a, b]) => proj.toLatLon(ux * a + nx * side * b, uy * a + ny * side * b));
        },
      });
    }
  }

  const offset = mulberry32(seed ^ 0x9e3779b9)() * ((2 * Math.PI) / LOBE_HEADINGS);
  for (const at of ["start", "end"] as const) {
    const ox = at === "start" ? 0 : e.x;
    const oy = at === "start" ? 0 : e.y;
    for (let h = 0; h < LOBE_HEADINGS; h++) {
      const theta = offset + (h * 2 * Math.PI) / LOBE_HEADINGS;
      shapes.push({
        name: `lobe-${at}@${Math.round((theta * 180) / Math.PI)}°`,
        vias: (scale) => {
          const circ = scale * (D / K - d);
          if (circ <= 0) return null;
          const R = circ / (2 * Math.PI);
          const cx = ox + R * Math.cos(theta);
          const cy = oy + R * Math.sin(theta);
          const back = theta + Math.PI;
          const ring = [1, 2].map((q) => {
            const a = back + (q * 2 * Math.PI) / 3;
            return proj.toLatLon(cx + R * Math.cos(a), cy + R * Math.sin(a));
          });
          // Lobe at the end: approach E, go round, finish at E.
          return at === "start" ? ring : [proj.toLatLon(ox, oy), ...ring];
        },
      });
    }
  }
  return shapes;
}

/**
 * Via-points on the circular arc of length L through (0,0) and (d,0), bulging towards +y.
 * Returns `n` points at evenly spaced arc fractions, as [along, across] coordinates.
 */
export function arcPoints(d: number, L: number, n: number): [number, number][] {
  const phi = arcAngle(d, L);
  const R = L / phi;
  // Centre below the chord midpoint by R·cos(φ/2) (above it when φ > π).
  const cx = d / 2;
  const cy = -R * Math.cos(phi / 2);
  const a0 = Math.atan2(0 - cy, 0 - cx);
  const out: [number, number][] = [];
  for (let k = 1; k <= n; k++) {
    // Clockwise from S over the top of the arc to E.
    const a = a0 - (k / (n + 1)) * phi;
    out.push([cx + R * Math.cos(a), cy + R * Math.sin(a)]);
  }
  return out;
}
